import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ToastrService } from 'ngx-toastr';
import { ContractType } from 'src/app/_enums/contract-type.enum';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { InsuranceFlowChannel } from 'src/app/_enums/insurance-flow/insurance-flow-channel.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { APIResponse } from 'src/app/_types/api-responses.types';
import {
  DocumentSummary,
  SignedContractDocument,
} from 'src/app/_types/document.types';
import {
  RenderCompanyMailResponse,
  SendInsuranceFlowContractResponse,
} from 'src/app/_types/insurance-flow.types';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { richTextRequired } from 'src/app/Dashboard/customer/communications/compose/compose-validators';
import { DocumentPickerComponent } from 'src/app/Dashboard/customer/communications/compose/document-picker/document-picker.component';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { DocumentSummaryComboboxComponent } from 'src/app/shared/components/document-summary-combobox/document-summary-combobox.component';
import { SignedContractDocumentComboboxComponent } from 'src/app/shared/components/signed-contract-document-combobox/signed-contract-document-combobox.component';
import { EDITOR_PLUGINS, EDITOR_TOOLBAR } from 'src/app/shared/editor-config';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { TaskDetailsService } from '../../../../task-details.service';
import { BaseStepperAction } from '../base-action';

@Component({
  selector: 'app-send-signed-proposal-action',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    IconsModule,
    ActionModalComponent,
    SignedContractDocumentComboboxComponent,
    DocumentSummaryComboboxComponent,
    EditorComponent,
    DocumentPickerComponent,
  ],
  templateUrl: './send-signed-proposal.component.html',
  styleUrls: ['./send-signed-proposal.component.css'],
})
export class SendSignedProposalComponent extends BaseStepperAction {
  get signedProposal(): ProposalDetails {
    return this.state?.policy;
  }

  get enabled() {
    return (
      this.state?.insuranceFlowData?.status ===
      InsuranceFlowStatus.PENDING_CONTRACT
    );
  }

  protected get familyMemberId(): string | null {
    return this.state?.insuranceFlowData?.memberId ?? null;
  }

  protected get userId(): string | null {
    return this.state?.insuranceFlowData?.userId ?? null;
  }

  submitted = false;

  /** TinyMCE lifecycle gate; see `ComposeModalBase.modalOpen`. */
  protected modalOpen = false;

  protected renderLoading = false;

  protected recipientEmail = '';

  @ViewChild('actionModal') modal: ActionModalComponent;

  protected readonly Channel = InsuranceFlowChannel;

  protected readonly ContractType = ContractType;

  protected readonly DocumentType = DocumentType;

  protected readonly editorPlugins = EDITOR_PLUGINS;

  protected readonly editorToolbar = EDITOR_TOOLBAR;

  protected readonly loadingText = $localize`Chargement…`;

  protected readonly form = new FormGroup({
    channel: new FormControl(InsuranceFlowChannel.IN_APP, {
      nonNullable: true,
    }),
    managementAgreement: new FormControl<SignedContractDocument | null>(null),
    identityCard: new FormControl<DocumentSummary | null>(null),
    signatureCertificate: new FormControl<DocumentSummary | null>(null),
    body: new FormControl('', { nonNullable: true }),
    attachments: new FormControl<DocumentSummary[]>([], { nonNullable: true }),
  });

  constructor(
    taskDetailsService: TaskDetailsService,
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly toastrService: ToastrService
  ) {
    super(taskDetailsService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.applyChannelValidators();
    this.subscription.add(
      this.form.controls.channel.valueChanges.subscribe(() => {
        this.applyChannelValidators();
      })
    );
  }

  protected get isInAppSend(): boolean {
    return this.form.controls.channel.value === InsuranceFlowChannel.IN_APP;
  }

  protected get submitDisabled(): boolean {
    return (
      this.form.invalid ||
      this.submitted ||
      (this.isInAppSend && this.renderLoading)
    );
  }

  protected openModal(): void {
    if (this.submitted) return;
    this.resetForm();
    this.modal.openModal();
    this.modalOpen = true;
    this.fetchRender();
  }

  /** Whatever dismissed the modal, the editor must not outlive it. */
  protected onClosed(): void {
    this.modalOpen = false;
    this.renderLoading = false;
  }

  sendSignedContract() {
    const proposalId = this.signedProposal?.proposalId;
    if (this.submitted || !proposalId) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const {
      channel,
      managementAgreement,
      identityCard,
      signatureCertificate,
      body,
      attachments,
    } = this.form.getRawValue();
    this.submitted = true;
    this.insuranceFlowService
      .sendContract({
        proposalId,
        channel,
        managementAgreementDocumentId: managementAgreement?.documentId ?? null,
        identityCardDocumentId: identityCard?.id ?? null,
        signatureCertificateDocumentId: signatureCertificate?.id ?? null,
        ...(channel === InsuranceFlowChannel.IN_APP ? { body } : {}),
        ...(attachments.length
          ? {
              attachmentDocumentIds: attachments.map((document) => document.id),
            }
          : {}),
      })
      .subscribe({
        next: (res) => {
          if (res.error) {
            this.displayError(res);
          } else {
            this.toastrService.success(this.successMessage(channel));
            this.modal?.closeModal();
            this.resetForm();
            this.refreshTaskDetails();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.displayError(err);
        },
      });
  }

  protected resetForm(): void {
    this.form.reset({ channel: InsuranceFlowChannel.IN_APP });
    this.applyChannelValidators();
    this.submitted = false;
    this.recipientEmail = '';
    this.renderLoading = false;
  }

  /** Prefills the editor and the recipient from what the send would mail. */
  private fetchRender(): void {
    const proposalId = this.signedProposal?.proposalId;
    if (!proposalId) return;

    this.renderLoading = true;
    this.subscription.add(
      this.insuranceFlowService.renderContract(proposalId).subscribe({
        next: (res) => {
          // A slow answer must not repopulate the next opening.
          if (!this.modalOpen) return;
          this.renderLoading = false;
          if (res.error || !res.data) {
            this.displayRenderError(res);
          } else {
            this.form.controls.body.setValue(res.data.body);
            this.recipientEmail = res.data.recipient?.email ?? '';
          }
        },
        error: (err: HttpErrorResponse) => {
          if (!this.modalOpen) return;
          this.renderLoading = false;
          this.displayRenderError(err);
        },
      })
    );
  }

  private successMessage(channel: InsuranceFlowChannel): string {
    switch (channel) {
      case InsuranceFlowChannel.INSURER_PLATFORM:
        return $localize`Contrat marqué comme envoyé via la plateforme de l'assureur`;
      case InsuranceFlowChannel.POSTAL:
        return $localize`Contrat marqué comme envoyé par courrier`;
      default:
        return $localize`Contrat envoyé à l'assureur`;
    }
  }

  private displayError(
    source: APIResponse<SendInsuranceFlowContractResponse> | HttpErrorResponse
  ): void {
    this.toastrService.error(
      getApiErrorMessage(
        source,
        $localize`Une erreur est survenue lors de l'envoi du contrat`
      )
    );
    this.submitted = false;
  }

  private displayRenderError(
    source: APIResponse<RenderCompanyMailResponse> | HttpErrorResponse
  ): void {
    this.toastrService.error(
      getApiErrorMessage(
        source,
        $localize`Une erreur est survenue lors du chargement de l'email`
      )
    );
  }

  private applyChannelValidators(): void {
    const { body } = this.form.controls;

    if (this.isInAppSend) {
      body.setValidators(richTextRequired);
    } else {
      // The prose survives the switch in case the agent comes back; the
      // hidden pickers do not, so a hidden pick is never sent along.
      body.clearValidators();
      this.form.controls.managementAgreement.reset(null);
      this.form.controls.identityCard.reset(null);
      this.form.controls.signatureCertificate.reset(null);
      this.form.controls.attachments.reset();
    }
    body.updateValueAndValidity();
  }
}
